'use client';

import * as ENGINE from '@gnsx/genesys.js';
import * as THREE from 'three';

import {
  type ActorQueryShapeField,
  type ActorQueryShapeOptions,
  serializeActorForQuery,
} from './actor-query-serialization';
import { executeCreateManyAction } from './create-many-payload';

import type { ActorAction, ActorActionInput, ActorActionResult } from './editor-api-types';
import type { IOutlinerNode } from '@gnsx/genesys.js';

export type {
  ActorAction,
  ActorActionInput,
  ActorActionResult,
  CreateManyCounts,
  CreatedActorMapping,
  CreatedComponentMapping,
} from './editor-api-types';

import { globalStore } from '@/app/globalStore';
import { nameHelpers, objectActions, sceneHandler } from '@/editor/core';
import { editorSave, setPlayModeActive } from '@/editor/core/actions/editorActions';
import { moveOutlinerNodes, renameSelectedNodes } from '@/editor/core/actions/outlinerActions';
import { applyToPrefabAsset, createPrefabFromObject, ensurePrefabAsset, unlinkPrefabFromActor } from '@/editor/core/actions/prefabActions';
import { focusSelectedObjects } from '@/editor/core/actions/sceneActions';
import { getGeometryCatalogSnapshot, MCP_PRIMITIVE_TYPE_SET } from '@/editor/core/geometryCatalog';
import { normalizePrefabAssetPath, resolvePrefabCreateDirectory } from '@/editor/core/prefabActionHelpers';
import { setPropertyValueDirect, UniversalAccessor } from '@/editor/core/universalAccessor';
import { assetsStore, editorStore } from '@/editor/store';
import { applyDefaultOutlinerPlacementIntent, captureDefaultOutlinerPlacementIntent } from '@/editor/store/sceneStore';



export type SceneGraphNode = {
  id: string;
  name: string;
  nodeType: string;
  parentId: string | null;
  childIds: string[];
};

function serializeDescribe(value: unknown, options?: { includeComponentsDetails?: boolean }): unknown {
  if (value !== null && typeof value === 'object' && typeof (value as { describe?: (o?: unknown) => unknown }).describe === 'function') {
    return (value as { describe: (o?: unknown) => unknown }).describe(options);
  }
  return value;
}

/** Strip values that cannot cross Electron IPC (structured clone / JSON). */
function toPipeSafeValue<T>(value: T): T {
  try {
    return structuredClone(value);
  } catch {
    // Fall back to JSON-safe sanitisation when structured clone fails.
  }

  const seen = new WeakSet<object>();

  try {
    return JSON.parse(JSON.stringify(value, (_key, raw) => {
      if (typeof raw === 'function' || typeof raw === 'symbol') {
        return undefined;
      }
      if (typeof raw === 'bigint') {
        return raw.toString();
      }
      if (raw !== null && typeof raw === 'object') {
        if (seen.has(raw)) {
          return '[Circular]';
        }
        seen.add(raw);
      }
      return raw;
    })) as T;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      error: 'Failed to serialise editor query result for IPC',
      reason: message,
    } as T;
  }
}

function getOutlinerState(): IOutlinerNode[] {
  const outliner = editorStore.scene.outliner;
  const rootNode = outliner.rootNode;
  if (!rootNode) return [];

  const nodes: IOutlinerNode[] = [];

  const traverse = (node: { id: string; name: string; nodeType: string; children: readonly { id: string }[] }, parentId: string | null) => {
    nodes.push({
      id: node.id,
      name: node.name,
      nodeType: node.nodeType as IOutlinerNode['nodeType'],
      parentId,
      childIds: node.children.map(c => c.id),
    });
    for (const child of node.children) {
      const childNode = outliner.getNodeById(child.id);
      if (childNode) {
        traverse(childNode, node.id);
      }
    }
  };

  traverse(rootNode, null);
  return nodes;
}

function getEditorStateSnapshot() {
  const hasProject = editorStore.project.isProjectOpened;
  const hasWorld = !!editorStore.scene.world;
  const hasCurrentScene = !!editorStore.project.currentScene;
  const blockingReasons: string[] = [];

  if (!hasProject) {
    blockingReasons.push('No project is currently open.');
  }

  if (!hasCurrentScene) {
    blockingReasons.push('No scene is currently active.');
  }

  if (!hasWorld) {
    blockingReasons.push('Editor world is not loaded.');
  }

  if (editorStore.isBuildingProject) {
    blockingReasons.push('Project build is currently running.');
  }

  if (editorStore.isSaving) {
    blockingReasons.push('Project save is currently running.');
  }

  if (editorStore.scene.isResettingWorld) {
    blockingReasons.push('Scene world reset is in progress.');
  }

  if (editorStore.scene.isCreating) {
    blockingReasons.push('Scene creation is in progress.');
  }

  if (editorStore.scene.isPlayModeStarting) {
    blockingReasons.push('Play mode is starting.');
  }

  const isBusy = editorStore.isBuildingProject
    || editorStore.isSaving
    || editorStore.scene.isResettingWorld
    || editorStore.scene.isCreating
    || editorStore.scene.isPlayModeStarting;

  return {
    editorConnected: true,
    editorReady: hasProject && hasWorld,
    blockingReasons,
    activeScenePath: editorStore.project.currentScene ?? null,
    isPlayMode: editorStore.scene.isPlayMode,
    isPlayModeStarting: editorStore.scene.isPlayModeStarting,
    isBusy,
    busyReasons: isBusy ? blockingReasons : [],
  };
}

type CameraControlsLike = {
  distance?: number;
  getTarget?: (out: THREE.Vector3) => THREE.Vector3;
  getPosition?: (out: THREE.Vector3) => THREE.Vector3;
};

function getViewportSnapshot() {
  const sceneState = getEditorStateSnapshot();
  const camera = editorStore.scene.camera;
  const renderer = editorStore.scene.renderer;
  const rendererType = editorStore.scene.rendererType;
  const controls = editorStore.scene.cameraControls as CameraControlsLike | null;
  const activeScenePath = editorStore.project.currentScene ?? null;
  const projectionMode = camera instanceof THREE.PerspectiveCamera
    ? 'perspective'
    : camera instanceof THREE.OrthographicCamera
      ? 'orthographic'
      : 'unknown';
  const hasViewport = camera != null && renderer != null;

  if (!hasViewport) {
    return {
      source: 'unavailable',
      operation: 'getViewport',
      status: 'not_connected',
      connected: false,
      resultIsAuthoritative: false,
      providerStatus: 'unavailable',
      provider: null,
      editorConnected: sceneState.editorConnected,
      editorBlockingReasons: sceneState.blockingReasons,
      message: 'Viewport camera or renderer is not connected in this runtime.',
      recoveryHint: 'Ensure the editor viewport is mounted and rendering before calling getViewport.',
      viewport: null,
    };
  }

  let target: [number, number, number] | null = null;
  let distance: number | null = null;
  if (controls) {
    try {
      if (typeof controls.getTarget === 'function') {
        const resolvedTarget = controls.getTarget(new THREE.Vector3());
        target = [resolvedTarget.x, resolvedTarget.y, resolvedTarget.z];
      }
      if (typeof controls.distance === 'number' && Number.isFinite(controls.distance)) {
        distance = controls.distance;
      } else if (typeof controls.getPosition === 'function' && target) {
        const resolvedPosition = controls.getPosition(new THREE.Vector3());
        const targetVector = new THREE.Vector3(target[0], target[1], target[2]);
        distance = resolvedPosition.distanceTo(targetVector);
      }
    } catch (error) {
      console.warn('[EditorAPI] Failed to read camera controls viewport state:', error);
    }
  }

  let canvasSize: { width: number; height: number } | null = null;
  try {
    // Read size from the native Three renderer (IGenesysRenderer itself exposes
    // setSize but not getSize); mirrors the access pattern in sceneStore.
    const nativeRenderer = renderer.asWebGL() ?? renderer.asWebGPU();
    if (nativeRenderer) {
      const renderSize = nativeRenderer.getSize(new THREE.Vector2());
      canvasSize = {
        width: Number.isFinite(renderSize.x) ? renderSize.x : 0,
        height: Number.isFinite(renderSize.y) ? renderSize.y : 0,
      };
    }
  } catch (error) {
    console.warn('[EditorAPI] Failed to read viewport canvas size:', error);
  }

  const nearFar = camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera
    ? {
      near: Number.isFinite(camera.near) ? camera.near : null,
      far: Number.isFinite(camera.far) ? camera.far : null,
    }
    : {
      near: null,
      far: null,
    };

  return {
    source: 'editor',
    operation: 'getViewport',
    status: 'connected',
    connected: true,
    resultIsAuthoritative: true,
    providerStatus: 'connected',
    provider: 'editor-viewport',
    editorConnected: sceneState.editorConnected,
    editorBlockingReasons: sceneState.blockingReasons,
    message: 'Viewport context is connected.',
    recoveryHint: null,
    viewport: {
      activeScenePath,
      rendererType,
      isPlayMode: editorStore.scene.isPlayMode,
      canvasSize,
      camera: {
        projectionMode,
        position: [camera.position.x, camera.position.y, camera.position.z] as [number, number, number],
        target,
        distance,
        near: nearFar.near,
        far: nearFar.far,
        fov: camera instanceof THREE.PerspectiveCamera && Number.isFinite(camera.fov) ? camera.fov : null,
        zoom: camera instanceof THREE.OrthographicCamera && Number.isFinite(camera.zoom) ? camera.zoom : null,
      },
    },
  };
}

function getSceneSummarySnapshot() {
  const graph = getOutlinerState();
  const selectedActorIds = editorStore.scene.selectedActors.map(actor => actor.uuid);
  return {
    activeScenePath: editorStore.project.currentScene ?? null,
    actorCount: graph.length,
    selectedActorIds,
  };
}

function getBuildErrorsSnapshot() {
  const errors = globalStore.buildErrors.slice();
  const errorCount = errors.length;
  return {
    source: 'editor',
    operation: 'getBuildErrors',
    status: 'connected',
    connected: true,
    resultIsAuthoritative: true,
    provider: 'editor-errors-store',
    providerStatus: 'connected',
    editorConnected: true,
    errors,
    warnings: [],
    errorCount,
    message: errorCount > 0
      ? `${errorCount} build error${errorCount === 1 ? '' : 's'} reported.`
      : 'No build errors reported.',
    recoveryHint: errorCount > 0
      ? 'Fix build errors before entering play mode or shipping changes.'
      : null,
  };
}

function getConsoleErrorsSnapshot() {
  const errors = globalStore.allPlayModeErrors.slice();
  const errorCount = errors.length;
  return {
    source: 'editor',
    operation: 'getConsole',
    status: 'connected',
    connected: true,
    resultIsAuthoritative: true,
    provider: 'editor-errors-store',
    providerStatus: 'connected',
    editorConnected: true,
    errors,
    warnings: [],
    errorCount,
    message: errorCount > 0
      ? `${errorCount} editor/play-mode console error${errorCount === 1 ? '' : 's'} reported.`
      : 'No editor/play-mode console errors reported.',
    recoveryHint: errorCount > 0
      ? 'Inspect console output and re-run diagnostics after fixing the reported issues.'
      : null,
  };
}

type ComponentAction = 'add' | 'setProperties' | 'setEnabled';

type ComponentActionInput = {
  action: ComponentAction;
  actorId?: string;
  componentId?: string;
  parentComponentId?: string;
  className?: string;
  properties?: Record<string, unknown>;
  enabled?: boolean;
  dryRun?: boolean;
};

type ComponentActionResult = {
  ok: boolean;
  action: ComponentAction;
  mode: 'dryRun' | 'apply';
  status: 'blocked' | 'planned' | 'applied';
  plannedChanges: unknown[];
  affectedComponentIds: string[];
  missingComponentIds: string[];
  undoGroupId: string | null;
  undoUnavailableReason: string | null;
  warnings: string[];
};

type EditorAction = 'frameSelection' | 'enterPlayMode' | 'exitPlayMode' | 'captureScreenshot';

type EditorActionInput = {
  action: EditorAction;
  dryRun?: boolean;
  scenePath?: string;
  imageType?: string;
  imageQuality?: number;
  includeImage?: boolean;
  filename?: string;
  width?: number;
  height?: number;
  updateThumbnails?: boolean;
};

type EditorActionResult = {
  ok: boolean;
  action: EditorAction;
  mode: 'dryRun' | 'apply';
  status: 'blocked' | 'planned' | 'applied' | 'initiated';
  plannedChanges: unknown[];
  undoGroupId: null;
  undoUnavailableReason: string;
  warnings: string[];
  screenshot?: {
    scenePath: string | null;
    projectScreenshotKey: string | null;
    sceneScreenshotKey: string | null;
    projectScreenshotPath: string | null;
    sceneScreenshotPath: string | null;
    screenshotPath: string | null;
    requestedFilename: string | null;
    captureWidth: number | null;
    captureHeight: number | null;
    imageType: string;
    imageByteLength: number | null;
    imageIncluded: boolean;
    imageBase64: string | null;
  };
};

type SceneAction = 'open' | 'save' | 'setActive';

type SceneActionInput = {
  action: SceneAction;
  scenePath?: string;
  dryRun?: boolean;
};

type SceneActionResult = {
  ok: boolean;
  action: SceneAction;
  mode: 'dryRun' | 'apply';
  status: 'blocked' | 'planned' | 'applied';
  plannedChanges: unknown[];
  undoGroupId: null;
  undoUnavailableReason: string;
  warnings: string[];
};

type PrefabAction = 'createFromActor' | 'instantiate' | 'apply' | 'unpack';

type PrefabActionInput = {
  action: PrefabAction;
  actorId?: string;
  prefabPath?: string;
  parentPath?: string;
  preferredName?: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
  dryRun?: boolean;
};

type PrefabActionResult = {
  ok: boolean;
  action: PrefabAction;
  mode: 'dryRun' | 'apply';
  status: 'blocked' | 'planned' | 'applied';
  plannedChanges: unknown[];
  prefabPath?: string | null;
  affectedActorIds: string[];
  missingActorIds: string[];
  undoGroupId: string | null;
  undoUnavailableReason: string | null;
  warnings: string[];
};

function isSceneMutatingAction(action: ActorAction): boolean {
  return action === 'rename' || action === 'create' || action === 'createMany' || action === 'duplicate' || action === 'delete' || action === 'setTransform' || action === 'reparent';
}

function toVector3(values: [number, number, number] | undefined): THREE.Vector3 | undefined {
  return values ? new THREE.Vector3(values[0], values[1], values[2]) : undefined;
}

function toEuler(values: [number, number, number] | undefined, order?: THREE.EulerOrder): THREE.Euler | undefined {
  return values ? new THREE.Euler(values[0], values[1], values[2], order) : undefined;
}

function buildTransform(input: {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
}, rotationOrder?: THREE.EulerOrder): { position?: THREE.Vector3; rotation?: THREE.Euler; scale?: THREE.Vector3 } {
  return {
    position: toVector3(input.position),
    rotation: toEuler(input.rotation, rotationOrder),
    scale: toVector3(input.scale),
  };
}

const CREATE_POSITION_VERIFY_TOLERANCE = 1e-4;

function verifyCreatedPosition(actor: ENGINE.Actor, requested?: THREE.Vector3): string | null {
  if (!requested) return null;
  const actual = actor.getWorldPosition();
  if (
    Math.abs(actual.x - requested.x) > CREATE_POSITION_VERIFY_TOLERANCE
    || Math.abs(actual.y - requested.y) > CREATE_POSITION_VERIFY_TOLERANCE
    || Math.abs(actual.z - requested.z) > CREATE_POSITION_VERIFY_TOLERANCE
  ) {
    return `Created actor position (${actual.x}, ${actual.y}, ${actual.z}) does not match requested (${requested.x}, ${requested.y}, ${requested.z}).`;
  }
  return null;
}

function getUndoGroupResult(
  undoRedo: typeof editorStore.undoRedoManager,
  beforeIndex: number,
  unavailableReason: string,
): { undoGroupId: string | null; undoUnavailableReason: string | null } {
  const afterIndex = undoRedo.getCurrentAction();
  const didCreateUndo = afterIndex > beforeIndex;
  return {
    undoGroupId: didCreateUndo ? `history-${afterIndex}` : null,
    undoUnavailableReason: didCreateUndo ? null : unavailableReason,
  };
}

async function waitForUndoCommit(): Promise<void> {
  await Promise.resolve();
  await new Promise(resolve => setTimeout(resolve, 0));
}

function getActorDisplayName(actorId: string, fallback: string): string {
  return editorStore.scene.outliner.getNodeById(actorId)?.name
    ?? editorStore.scene.world?.getActorByUuid(actorId)?.name
    ?? fallback;
}

function setActorDisplayName(actor: Parameters<typeof nameHelpers.setCustomDisplayName>[0], name: string): void {
  const selectedEditable = editorStore.scene.getSelectedObjectById(actor.uuid);
  if (selectedEditable?.asActor) {
    selectedEditable.setName(name);
  } else {
    nameHelpers.setCustomDisplayName(actor, name);
  }
  editorStore.scene.outliner.getNodeById(actor.uuid)?.setName(name);
}

function notifyActorTransformChanged(actor: { rootComponent?: unknown }, transform: {
  position?: THREE.Vector3;
  rotation?: THREE.Euler;
  scale?: THREE.Vector3;
}): void {
  const rootComponent = actor.rootComponent;
  if (!rootComponent) return;

  try {
    if (transform.position) {
      objectActions.notifyEditorPropertyChanged(rootComponent, 'position', transform.position);
    }
    if (transform.rotation) {
      objectActions.notifyEditorPropertyChanged(rootComponent, 'rotation', transform.rotation);
    }
    if (transform.scale) {
      objectActions.notifyEditorPropertyChanged(rootComponent, 'scale', transform.scale);
    }
  } catch (error) {
    console.warn('[EditorAPI] Failed to notify actor transform change:', error);
  }
}

function getPathValue(target: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((value, segment) => {
    if (!value || typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[segment];
  }, target);
}

function setComponentProperties(component: unknown, properties: Record<string, unknown>): string[] {
  const changed: string[] = [];
  for (const [path, value] of Object.entries(properties)) {
    if (!path.trim()) continue;
    if (setPropertyValueDirect(component, path, value)) {
      objectActions.notifyEditorPropertyChanged(component, path, value);
      changed.push(path);
    }
  }
  return changed;
}

function getComponent(actorId: string, componentId: string): ENGINE.SceneComponent | null {
  return UniversalAccessor.forComponent(actorId, componentId).resolveComponent();
}

async function saveAfterSceneMutation(
  action: ActorAction,
  mode: 'dryRun' | 'apply',
  result: ActorActionResult,
): Promise<string[]> {
  if (mode !== 'apply' || !result.ok || result.status !== 'applied' || !isSceneMutatingAction(action)) {
    return result.warnings;
  }

  try {
    const saved = await editorSave(false, false);
    if (!saved) {
      return [...result.warnings, 'Scene mutation applied but save did not complete.'];
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return [...result.warnings, `Scene mutation applied but save failed: ${message}`];
  }

  return result.warnings;
}

type UndoGroupActionResult = {
  ok: boolean;
  action: 'begin' | 'end';
  undoGroupId: string | null;
  undoUnavailableReason: string | null;
  warnings: string[];
};

function executeUndoGroupAction(input: { action: 'begin' | 'end'; name?: string }): UndoGroupActionResult {
  const undoRedo = editorStore.undoRedoManager;
  if (input.action === 'begin') {
    if (undoRedo.isGroupOpen) {
      return {
        ok: false,
        action: 'begin',
        undoGroupId: null,
        undoUnavailableReason: 'An undo group is already open.',
        warnings: ['An undo group is already open.'],
      };
    }
    undoRedo.beginGroup(input.name?.trim() || 'MCP Batch');
    return {
      ok: true,
      action: 'begin',
      undoGroupId: null,
      undoUnavailableReason: null,
      warnings: [],
    };
  }

  if (!undoRedo.isGroupOpen) {
    return {
      ok: false,
      action: 'end',
      undoGroupId: null,
      undoUnavailableReason: 'No undo group is open.',
      warnings: ['No undo group is open.'],
    };
  }

  const beforeIndex = undoRedo.getCurrentAction();
  undoRedo.endGroup();
  const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Batch undo group did not register an undo entry.');
  return {
    ok: true,
    action: 'end',
    undoGroupId: undo.undoGroupId,
    undoUnavailableReason: undo.undoUnavailableReason,
    warnings: [],
  };
}

async function executeActorAction(input: ActorActionInput): Promise<ActorActionResult> {
  const dryRun = input.dryRun ?? false;
  const mode = dryRun ? 'dryRun' : 'apply';
  const world = editorStore.scene.world;

  if (!world) {
    return {
      ok: false,
      action: input.action,
      mode,
      status: 'blocked',
      plannedChanges: [],
      affectedActorIds: [],
      missingActorIds: [],
      undoGroupId: null,
      undoUnavailableReason: 'Editor world is not loaded.',
      warnings: ['Editor world is not loaded.'],
    };
  }

  if (input.action === 'rename') {
    const actorId = input.actorId;
    const nextName = input.name?.trim();
    if (!actorId || !nextName) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'Rename requires actorId and name.',
        warnings: ['Rename requires actorId and name.'],
      };
    }

    const actor = world.getActorByUuid(actorId);
    if (!actor) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedActorIds: [],
        missingActorIds: [actorId],
        undoGroupId: null,
        undoUnavailableReason: 'Target actor was not found.',
        warnings: [`Actor ${actorId} was not found.`],
      };
    }

    const oldName = actor.name;
    const plannedChanges = [{ kind: 'rename', actorId, from: oldName, to: nextName }];
    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        affectedActorIds: [actorId],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: null,
        warnings: [],
      };
    }

    const undoRedo = editorStore.undoRedoManager;
    const beforeIndex = undoRedo.getCurrentAction();
    editorStore.scene.selectObject(actor);
    renameSelectedNodes(nextName);
    const afterIndex = undoRedo.getCurrentAction();
    const renamedName = world.getActorByUuid(actorId)?.name ?? nextName;
    const didCreateUndo = afterIndex > beforeIndex;

    const result: ActorActionResult = {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges: [{ kind: 'rename', actorId, from: oldName, to: renamedName }],
      affectedActorIds: [actorId],
      missingActorIds: [],
      undoGroupId: didCreateUndo ? `history-${afterIndex}` : null,
      undoUnavailableReason: didCreateUndo ? null : 'Rename did not create an undo group.',
      warnings: [],
    };
    return { ...result, warnings: await saveAfterSceneMutation(input.action, mode, result) };
  }

  if (input.action === 'createMany') {
    const result = await executeCreateManyAction({
      payload: input.payload,
      dryRun,
    });
    return { ...result, warnings: await saveAfterSceneMutation(input.action, mode, result) };
  }

  if (input.action === 'create') {
    const primitiveType = input.primitiveType?.trim() ?? null;
    const classNameInput = input.className?.trim() ?? null;
    const assetPathInput = input.assetPath?.trim() ?? null;

    const createModeCount = [primitiveType, classNameInput, assetPathInput].filter(Boolean).length;
    if (createModeCount !== 1) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'create accepts exactly one of className, primitiveType, or assetPath.',
        warnings: ['create accepts exactly one of className, primitiveType, or assetPath.'],
      };
    }

    const newName = input.name?.trim() ?? null;
    const transform = buildTransform(input);

    if (assetPathInput) {
      const plannedChanges = [{
        kind: 'create',
        assetPath: assetPathInput,
        name: newName ?? assetPathInput.split('/').pop() ?? 'Model',
        transform,
      }];

      if (dryRun) {
        return {
          ok: true,
          action: input.action,
          mode,
          status: 'planned',
          plannedChanges,
          affectedActorIds: [],
          missingActorIds: [],
          undoGroupId: null,
          undoUnavailableReason: null,
          warnings: [],
        };
      }

      const undoRedo = editorStore.undoRedoManager;
      const beforeIndex = undoRedo.getCurrentAction();
      const isInsideGroup = undoRedo.isGroupOpen;
      if (!isInsideGroup) undoRedo.beginGroup('Create Actor');

      let actor: ENGINE.Actor | null = null;
      let placedActor: ENGINE.Actor | null = null;
      let blockedResult: ActorActionResult | null = null;
      const displayLabel = newName ?? assetPathInput.split('/').pop()?.replace(/\.[^.]+$/, '') ?? 'Model';

      const tryCleanupPlacedActor = () => {
        if (!placedActor || actor) return;
        try {
          world.removeActors(placedActor);
        } catch (cleanupError) {
          console.warn('[EditorAPI] Failed to clean up unregistered model actor after create failure:', cleanupError);
        }
      };

      try {
        const position = transform.position ?? new THREE.Vector3(0, 0, 0);
        const placedActors = await ENGINE.WorldCommands.placeGltfs({
          world,
          gltfs: [{
            path: assetPathInput,
            transform: {
              position,
              rotation: transform.rotation,
              scale: transform.scale,
            },
            miscInfo: newName ? { name: newName } : undefined,
          }],
        });
        placedActor = placedActors[0] ?? null;
        if (!placedActor) {
          blockedResult = {
            ok: false,
            action: input.action,
            mode,
            status: 'blocked',
            plannedChanges,
            affectedActorIds: [],
            missingActorIds: [],
            undoGroupId: null,
            undoUnavailableReason: `Failed to create model actor from asset: ${assetPathInput}.`,
            warnings: [`Failed to create model actor from asset: ${assetPathInput}.`],
          };
        } else {
          actor = editorStore.scene.addObject(placedActor, {}, true);
          if (!actor) {
            tryCleanupPlacedActor();
            blockedResult = {
              ok: false,
              action: input.action,
              mode,
              status: 'blocked',
              plannedChanges,
              affectedActorIds: [],
              missingActorIds: [],
              undoGroupId: null,
              undoUnavailableReason: `Failed to register model actor from asset: ${assetPathInput}.`,
              warnings: [`Failed to register model actor from asset: ${assetPathInput}.`],
            };
          } else if (newName) {
            const oldName = getActorDisplayName(actor.uuid, displayLabel);
            const existingNames = nameHelpers.collectOutlinerNames(editorStore.scene.outliner.rootNode ?? undefined, { omitNodeId: actor.uuid });
            const uniqueName = nameHelpers.makeUniqueName(newName, existingNames);
            await undoRedo.recordAction(
              'Rename Created Actor',
              () => { setActorDisplayName(actor!, uniqueName); },
              () => { setActorDisplayName(actor!, oldName); },
            );
          }
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        blockedResult = {
          ok: false,
          action: input.action,
          mode,
          status: 'blocked',
          plannedChanges,
          affectedActorIds: [],
          missingActorIds: [],
          undoGroupId: null,
          undoUnavailableReason: `Failed to create model actor: ${message}`,
          warnings: [`Failed to create model actor: ${message}`],
        };
      } finally {
        if (blockedResult) {
          tryCleanupPlacedActor();
        }
        if (!isInsideGroup) undoRedo.endGroup();
      }

      if (blockedResult) {
        return blockedResult;
      }

      if (!actor) {
        return {
          ok: false,
          action: input.action,
          mode,
          status: 'blocked',
          plannedChanges,
          affectedActorIds: [],
          missingActorIds: [],
          undoGroupId: null,
          undoUnavailableReason: `Failed to register model actor from asset: ${assetPathInput}.`,
          warnings: [`Failed to register model actor from asset: ${assetPathInput}.`],
        };
      }

      const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Create did not register an undo entry.');
      const createWarnings: string[] = [];
      const positionWarning = verifyCreatedPosition(actor, transform.position);
      if (positionWarning) createWarnings.push(positionWarning);
      const result: ActorActionResult = {
        ok: true,
        action: input.action,
        mode,
        status: 'applied',
        plannedChanges: [{
          kind: 'create',
          assetPath: assetPathInput,
          actorId: actor.uuid,
          rootComponentId: actor.rootComponent?.uuid ?? null,
          name: getActorDisplayName(actor.uuid, newName ?? displayLabel),
          transform,
        }],
        affectedActorIds: [actor.uuid],
        missingActorIds: [],
        rootComponentId: actor.rootComponent?.uuid ?? null,
        undoGroupId: undo.undoGroupId,
        undoUnavailableReason: undo.undoUnavailableReason,
        warnings: createWarnings,
      };
      return { ...result, warnings: [...createWarnings, ...(await saveAfterSceneMutation(input.action, mode, result))] };
    }

    if (primitiveType) {
      if (!MCP_PRIMITIVE_TYPE_SET.has(primitiveType)) {
        return {
          ok: false,
          action: input.action,
          mode,
          status: 'blocked',
          plannedChanges: [],
          affectedActorIds: [],
          missingActorIds: [],
          undoGroupId: null,
          undoUnavailableReason: `Unsupported primitive type: ${primitiveType}.`,
          warnings: [`Unsupported primitive type: ${primitiveType}.`],
        };
      }

      const displayLabel = primitiveType.charAt(0).toUpperCase() + primitiveType.slice(1);
      const plannedChanges = [{
        kind: 'create',
        primitiveType,
        name: newName ?? displayLabel,
        transform,
      }];

      if (dryRun) {
        return {
          ok: true,
          action: input.action,
          mode,
          status: 'planned',
          plannedChanges,
          affectedActorIds: [],
          missingActorIds: [],
          undoGroupId: null,
          undoUnavailableReason: null,
          warnings: [],
        };
      }

      const undoRedo = editorStore.undoRedoManager;
      const beforeIndex = undoRedo.getCurrentAction();
      const isInsideGroup = undoRedo.isGroupOpen;
      if (!isInsideGroup) undoRedo.beginGroup('Create Actor');

      let actor: ENGINE.Actor | null = null;
      let blockedResult: ActorActionResult | null = null;
      try {
        const position = transform.position ?? new THREE.Vector3(0, 0, 0);
        const placedActors = ENGINE.WorldCommands.placePrimitives({
          world,
          primitiveActors: [{
            transform: {
              position,
              rotation: transform.rotation,
              scale: transform.scale,
            },
            primitives: [{
              type: primitiveType,
              color: 0xffffff,
            }],
            miscInfo: newName ? { name: newName } : undefined,
          }],
        });
        const placedActor = placedActors[0];
        if (!placedActor) {
          blockedResult = {
            ok: false,
            action: input.action,
            mode,
            status: 'blocked',
            plannedChanges,
            affectedActorIds: [],
            missingActorIds: [],
            undoGroupId: null,
            undoUnavailableReason: `Failed to create primitive actor of type ${primitiveType}.`,
            warnings: [`Failed to create primitive actor of type ${primitiveType}.`],
          };
        } else {
          actor = editorStore.scene.addObject(placedActor, {}, true);
          if (!actor) {
            blockedResult = {
              ok: false,
              action: input.action,
              mode,
              status: 'blocked',
              plannedChanges,
              affectedActorIds: [],
              missingActorIds: [],
              undoGroupId: null,
              undoUnavailableReason: `Failed to register primitive actor of type ${primitiveType}.`,
              warnings: [`Failed to register primitive actor of type ${primitiveType}.`],
            };
          } else if (newName) {
            const oldName = getActorDisplayName(actor.uuid, displayLabel);
            const existingNames = nameHelpers.collectOutlinerNames(editorStore.scene.outliner.rootNode ?? undefined, { omitNodeId: actor.uuid });
            const uniqueName = nameHelpers.makeUniqueName(newName, existingNames);
            await undoRedo.recordAction(
              'Rename Created Actor',
              () => { setActorDisplayName(actor!, uniqueName); },
              () => { setActorDisplayName(actor!, oldName); },
            );
          }
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        blockedResult = {
          ok: false,
          action: input.action,
          mode,
          status: 'blocked',
          plannedChanges,
          affectedActorIds: [],
          missingActorIds: [],
          undoGroupId: null,
          undoUnavailableReason: `Failed to create primitive actor: ${message}`,
          warnings: [`Failed to create primitive actor: ${message}`],
        };
      } finally {
        if (!isInsideGroup) undoRedo.endGroup();
      }

      if (blockedResult) {
        return blockedResult;
      }

      if (!actor) {
        return {
          ok: false,
          action: input.action,
          mode,
          status: 'blocked',
          plannedChanges,
          affectedActorIds: [],
          missingActorIds: [],
          undoGroupId: null,
          undoUnavailableReason: `Failed to register primitive actor of type ${primitiveType}.`,
          warnings: [`Failed to register primitive actor of type ${primitiveType}.`],
        };
      }

      const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Create did not register an undo entry.');
      const createWarnings: string[] = [];
      const positionWarning = verifyCreatedPosition(actor, transform.position);
      if (positionWarning) createWarnings.push(positionWarning);
      const result: ActorActionResult = {
        ok: true,
        action: input.action,
        mode,
        status: 'applied',
        plannedChanges: [{
          kind: 'create',
          primitiveType,
          actorId: actor.uuid,
          rootComponentId: actor.rootComponent?.uuid ?? null,
          name: getActorDisplayName(actor.uuid, newName ?? displayLabel),
          transform,
        }],
        affectedActorIds: [actor.uuid],
        missingActorIds: [],
        rootComponentId: actor.rootComponent?.uuid ?? null,
        undoGroupId: undo.undoGroupId,
        undoUnavailableReason: undo.undoUnavailableReason,
        warnings: createWarnings,
      };
      return { ...result, warnings: [...createWarnings, ...(await saveAfterSceneMutation(input.action, mode, result))] };
    }

    const className = classNameInput || 'Actor';
    const plannedChanges = [{ kind: 'create', className, name: newName ?? className, transform }];

    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: null,
        warnings: [],
      };
    }

    const undoRedo = editorStore.undoRedoManager;
    const beforeIndex = undoRedo.getCurrentAction();
    const isInsideGroup = undoRedo.isGroupOpen;
    if (!isInsideGroup) undoRedo.beginGroup('Create Actor');

    let actor: ENGINE.Actor | null = null;
    try {
      actor = editorStore.scene.addObject(className, transform);
      if (!actor) {
        return {
          ok: false,
          action: input.action,
          mode,
          status: 'blocked',
          plannedChanges,
          affectedActorIds: [],
          missingActorIds: [],
          undoGroupId: null,
          undoUnavailableReason: `Failed to create actor of class ${className}.`,
          warnings: [`Failed to create actor of class ${className}.`],
        };
      }

      if (newName) {
        const oldName = getActorDisplayName(actor.uuid, className);
        const existingNames = nameHelpers.collectOutlinerNames(editorStore.scene.outliner.rootNode ?? undefined, { omitNodeId: actor.uuid });
        const uniqueName = nameHelpers.makeUniqueName(newName, existingNames);
        await undoRedo.recordAction(
          'Rename Created Actor',
          () => { setActorDisplayName(actor!, uniqueName); },
          () => { setActorDisplayName(actor!, oldName); },
        );
      }
    } finally {
      if (!isInsideGroup) undoRedo.endGroup();
    }

    const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Create did not register an undo entry.');
    const result: ActorActionResult = {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges: [{
        kind: 'create',
        className,
        actorId: actor.uuid,
        rootComponentId: actor.rootComponent?.uuid ?? null,
        name: getActorDisplayName(actor.uuid, newName ?? className),
        transform,
      }],
      affectedActorIds: [actor.uuid],
      missingActorIds: [],
      rootComponentId: actor.rootComponent?.uuid ?? null,
      undoGroupId: undo.undoGroupId,
      undoUnavailableReason: undo.undoUnavailableReason,
      warnings: [],
    };
    return { ...result, warnings: await saveAfterSceneMutation(input.action, mode, result) };
  }

  if (input.action === 'duplicate') {
    const requestedIds = input.actorIds ?? [];
    const resolvedActors = requestedIds
      .map(id => world.getActorByUuid(id))
      .filter((actor): actor is ENGINE.Actor => actor != null);
    const affectedActorIds = resolvedActors.map(actor => actor.uuid);
    const missingActorIds = requestedIds.filter(id => !affectedActorIds.includes(id));
    const plannedChanges = affectedActorIds.map(id => ({ kind: 'duplicate', actorId: id }));

    if (requestedIds.length === 0) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'No actor ids provided.',
        warnings: ['At least one actor id is required for duplicate.'],
      };
    }

    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        affectedActorIds,
        missingActorIds,
        undoGroupId: null,
        undoUnavailableReason: null,
        warnings: missingActorIds.length > 0 ? [`Some actor ids were not found: ${missingActorIds.join(', ')}`] : [],
      };
    }

    if (resolvedActors.length === 0) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges,
        affectedActorIds,
        missingActorIds,
        undoGroupId: null,
        undoUnavailableReason: 'None of the requested actors were found in the scene.',
        warnings: [`No valid actors found for ids: ${requestedIds.join(', ')}`],
      };
    }

    const undoRedo = editorStore.undoRedoManager;
    const beforeIndex = undoRedo.getCurrentAction();
    editorStore.scene.selectObjects(resolvedActors);
    editorStore.scene.duplicateSelectedObjectsOrSubobjects();
    await waitForUndoCommit();
    const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Duplicate did not register an undo entry.');
    const result: ActorActionResult = {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges,
      affectedActorIds,
      missingActorIds,
      undoGroupId: undo.undoGroupId,
      undoUnavailableReason: undo.undoUnavailableReason,
      warnings: missingActorIds.length > 0 ? [`Some actors were not found and were skipped: ${missingActorIds.join(', ')}`] : [],
    };
    return { ...result, warnings: await saveAfterSceneMutation(input.action, mode, result) };
  }

  if (input.action === 'reparent') {
    const requestedIds = input.actorIds ?? [];
    const targetParentId = input.targetParentId ?? null;
    const existingIds = requestedIds.filter(id => world.getActorByUuid(id));
    const missingActorIds = requestedIds.filter(id => !existingIds.includes(id));
    const plannedChanges = existingIds.map(id => ({ kind: 'reparent', actorId: id, targetParentId }));

    if (requestedIds.length === 0) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'No actor ids provided.',
        warnings: ['At least one actor id is required for reparent.'],
      };
    }

    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        affectedActorIds: existingIds,
        missingActorIds,
        undoGroupId: null,
        undoUnavailableReason: null,
        warnings: missingActorIds.length > 0 ? [`Some actor ids were not found: ${missingActorIds.join(', ')}`] : [],
      };
    }

    const undoRedo = editorStore.undoRedoManager;
    const beforeIndex = undoRedo.getCurrentAction();
    const movedIds = moveOutlinerNodes({ type: 'reparent', nodeIds: existingIds, targetParentId });
    await waitForUndoCommit();
    const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Reparent did not register an undo entry.');
    const result: ActorActionResult = {
      ok: movedIds.length > 0,
      action: input.action,
      mode,
      status: movedIds.length > 0 ? 'applied' : 'blocked',
      plannedChanges,
      affectedActorIds: movedIds,
      missingActorIds,
      undoGroupId: undo.undoGroupId,
      undoUnavailableReason: movedIds.length > 0 ? undo.undoUnavailableReason : 'No actors were reparented.',
      warnings: [
        ...(missingActorIds.length > 0 ? [`Some actors were not found and were skipped: ${missingActorIds.join(', ')}`] : []),
        ...(movedIds.length === 0 ? ['No actors were reparented. Check the target parent id.'] : []),
      ],
    };
    return { ...result, warnings: await saveAfterSceneMutation(input.action, mode, result) };
  }

  if (input.action === 'delete') {
    const requestedIds = input.actorIds ?? [];
    if (requestedIds.length === 0) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'No actor ids provided.',
        warnings: ['At least one actor id is required for delete.'],
      };
    }

    const resolvedActors = requestedIds
      .map(id => world.getActorByUuid(id))
      .filter(a => a != null);
    const affectedActorIds = resolvedActors.map(a => a.uuid);
    const missingActorIds = requestedIds.filter(id => !affectedActorIds.includes(id));
    const plannedChanges = affectedActorIds.map(id => ({
      kind: 'delete',
      actorId: id,
      name: world.getActorByUuid(id)?.name ?? id,
    }));

    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        affectedActorIds,
        missingActorIds,
        undoGroupId: null,
        undoUnavailableReason: null,
        warnings: missingActorIds.length > 0 ? [`Some actor ids were not found: ${missingActorIds.join(', ')}`] : [],
      };
    }

    if (resolvedActors.length === 0) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges,
        affectedActorIds,
        missingActorIds,
        undoGroupId: null,
        undoUnavailableReason: 'None of the requested actors were found in the scene.',
        warnings: [`No valid actors found for ids: ${requestedIds.join(', ')}`],
      };
    }

    const undoRedo = editorStore.undoRedoManager;
    const beforeIndex = undoRedo.getCurrentAction();
    const isInsideGroup = undoRedo.isGroupOpen;
    if (!isInsideGroup) undoRedo.beginGroup('Delete Actors');
    try {
      editorStore.scene.removeObjects(...resolvedActors);
    } finally {
      if (!isInsideGroup) undoRedo.endGroup();
    }
    const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Delete did not register an undo entry.');

    const result: ActorActionResult = {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges,
      affectedActorIds,
      missingActorIds,
      undoGroupId: undo.undoGroupId,
      undoUnavailableReason: undo.undoUnavailableReason,
      warnings: missingActorIds.length > 0 ? [`Some actors were not found and were skipped: ${missingActorIds.join(', ')}`] : [],
    };
    return { ...result, warnings: await saveAfterSceneMutation(input.action, mode, result) };
  }

  if (input.action === 'setTransform') {
    const actorId = input.actorId;
    if (!actorId) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'setTransform requires actorId.',
        warnings: ['setTransform requires actorId.'],
      };
    }

    const actor = world.getActorByUuid(actorId);
    if (!actor) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedActorIds: [],
        missingActorIds: [actorId],
        undoGroupId: null,
        undoUnavailableReason: 'Target actor was not found.',
        warnings: [`Actor ${actorId} was not found.`],
      };
    }

    const hasPosition = Array.isArray(input.position) && input.position.length >= 3;
    const hasRotation = Array.isArray(input.rotation) && input.rotation.length >= 3;
    const hasScale = Array.isArray(input.scale) && input.scale.length >= 3;

    if (!hasPosition && !hasRotation && !hasScale) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedActorIds: [actorId],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'setTransform requires at least one of position, rotation, or scale.',
        warnings: ['At least one of position, rotation, or scale must be provided.'],
      };
    }

    const wPos = actor.getWorldPosition();
    const wRot = actor.getWorldRotation();
    const wScale = actor.getWorldScale();
    const prevPosition = { x: wPos.x, y: wPos.y, z: wPos.z };
    const prevRotation = { x: wRot.x, y: wRot.y, z: wRot.z, order: wRot.order };
    const prevScale = { x: wScale.x, y: wScale.y, z: wScale.z };

    const plannedChanges = [{
      kind: 'setTransform',
      actorId,
      from: { position: prevPosition, rotation: prevRotation, scale: prevScale },
      to: {
        position: hasPosition ? { x: input.position![0], y: input.position![1], z: input.position![2] } : prevPosition,
        rotation: hasRotation ? { x: input.rotation![0], y: input.rotation![1], z: input.rotation![2] } : prevRotation,
        scale: hasScale ? { x: input.scale![0], y: input.scale![1], z: input.scale![2] } : prevScale,
      },
    }];

    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        affectedActorIds: [actorId],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: null,
        warnings: [],
      };
    }

    const undoRedo = editorStore.undoRedoManager;
    const beforeIndex = undoRedo.getCurrentAction();
    const applyTransform = buildTransform(input, prevRotation.order);
    const undoTransform = {
      position: new THREE.Vector3(prevPosition.x, prevPosition.y, prevPosition.z),
      rotation: new THREE.Euler(prevRotation.x, prevRotation.y, prevRotation.z, prevRotation.order),
      scale: new THREE.Vector3(prevScale.x, prevScale.y, prevScale.z),
    };
    await undoRedo.recordAction(
      'Set Actor Transform',
      () => {
        actor.setWorldTransform(applyTransform);
        notifyActorTransformChanged(actor, applyTransform);
      },
      () => {
        actor.setWorldTransform(undoTransform);
        notifyActorTransformChanged(actor, undoTransform);
      },
    );

    const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Set transform did not register an undo entry.');
    const result: ActorActionResult = {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges,
      affectedActorIds: [actorId],
      missingActorIds: [],
      undoGroupId: undo.undoGroupId,
      undoUnavailableReason: undo.undoUnavailableReason,
      warnings: [],
    };
    return { ...result, warnings: await saveAfterSceneMutation(input.action, mode, result) };
  }

  const requestedActorIds = input.actorIds ?? [];
  if (requestedActorIds.length === 0) {
    return {
      ok: false,
      action: input.action,
      mode,
      status: 'blocked',
      plannedChanges: [],
      affectedActorIds: [],
      missingActorIds: [],
      undoGroupId: null,
      undoUnavailableReason: 'No actor ids were provided.',
      warnings: ['At least one actor id is required.'],
    };
  }

  const resolvedActors = requestedActorIds
    .map(actorId => world.getActorByUuid(actorId))
    .filter(actor => actor != null);
  const affectedActorIds = resolvedActors.map(actor => actor.uuid);
  const missingActorIds = requestedActorIds.filter(actorId => !affectedActorIds.includes(actorId));
  const plannedChanges = [{
    kind: input.action === 'focus' ? 'focusSelection' : 'selection',
    actorIds: affectedActorIds,
  }];

  if (dryRun) {
    return {
      ok: true,
      action: input.action,
      mode,
      status: 'planned',
      plannedChanges,
      affectedActorIds,
      missingActorIds,
      undoGroupId: null,
      undoUnavailableReason: 'Selection and focus changes are not currently tracked in undo history.',
      warnings: missingActorIds.length > 0 ? [`Some actor ids were not found: ${missingActorIds.join(', ')}`] : [],
    };
  }

  if (resolvedActors.length === 0) {
    return {
      ok: false,
      action: input.action,
      mode,
      status: 'blocked',
      plannedChanges,
      affectedActorIds,
      missingActorIds,
      undoGroupId: null,
      undoUnavailableReason: 'No valid actors were resolved for action apply.',
      warnings: [`No valid actor ids were found: ${requestedActorIds.join(', ')}`],
    };
  }

  if (resolvedActors.length === 1) {
    editorStore.scene.selectObject(resolvedActors[0]);
  } else {
    editorStore.scene.selectObjects(resolvedActors, false);
  }

  if (input.action === 'focus') {
    focusSelectedObjects();
  }

  return {
    ok: true,
    action: input.action,
    mode,
    status: 'applied',
    plannedChanges,
    affectedActorIds,
    missingActorIds,
    undoGroupId: null,
    undoUnavailableReason: 'Selection and focus changes are not currently tracked in undo history.',
    warnings: missingActorIds.length > 0 ? [`Some actor ids were not found: ${missingActorIds.join(', ')}`] : [],
  };
}

async function executeComponentAction(input: ComponentActionInput): Promise<ComponentActionResult> {
  const dryRun = input.dryRun ?? false;
  const mode = dryRun ? 'dryRun' : 'apply';
  const actorId = input.actorId;

  if (!actorId) {
    return {
      ok: false,
      action: input.action,
      mode,
      status: 'blocked',
      plannedChanges: [],
      affectedComponentIds: [],
      missingComponentIds: [],
      undoGroupId: null,
      undoUnavailableReason: 'Component action requires actorId.',
      warnings: ['Component action requires actorId.'],
    };
  }

  const actor = editorStore.scene.world?.getActorByUuid(actorId);
  if (!actor) {
    return {
      ok: false,
      action: input.action,
      mode,
      status: 'blocked',
      plannedChanges: [],
      affectedComponentIds: [],
      missingComponentIds: [],
      undoGroupId: null,
      undoUnavailableReason: 'Target actor was not found.',
      warnings: [`Actor ${actorId} was not found.`],
    };
  }

  if (input.action === 'add') {
    const className = input.className?.trim();
    if (!className) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedComponentIds: [],
        missingComponentIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'add requires className.',
        warnings: ['add requires className.'],
      };
    }

    const parentComponentId = input.parentComponentId ?? actor.rootComponent?.uuid;
    const plannedChanges = [{ kind: 'addComponent', actorId, parentComponentId, className }];
    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        affectedComponentIds: [],
        missingComponentIds: [],
        undoGroupId: null,
        undoUnavailableReason: null,
        warnings: [],
      };
    }

    editorStore.scene.selectObject(actor);
    const undoRedo = editorStore.undoRedoManager;
    const beforeIndex = undoRedo.getCurrentAction();
    const actorAccessor = UniversalAccessor.forActor(actorId);
    const actorSnapshot = objectActions.takeActorSnapshot(actorAccessor);
    const parentAccessor = parentComponentId ? UniversalAccessor.forComponent(actorId, parentComponentId) : actorAccessor;
    const componentId = ENGINE.shortUUID();
    await undoRedo.recordAction(
      'Add Component',
      () => {
        objectActions.addComponentInternal(parentAccessor, className, true, componentId);
        editorStore.scene.updateInspector();
      },
      () => {
        objectActions.applyActorSnapshot(actorAccessor, actorSnapshot);
        editorStore.scene.updateInspector();
      },
    );
    const component = getComponent(actorId, componentId);
    const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Add component did not register an undo entry.');
    const result: ComponentActionResult = {
      ok: !!component,
      action: input.action,
      mode,
      status: component ? 'applied' : 'blocked',
      plannedChanges,
      affectedComponentIds: component ? [component.uuid] : [],
      missingComponentIds: [],
      undoGroupId: undo.undoGroupId,
      undoUnavailableReason: component ? undo.undoUnavailableReason : 'Component could not be created.',
      warnings: component ? [] : [`Failed to add component of class ${className}.`],
    };
    return { ...result, warnings: await saveAfterComponentMutation(mode, result) };
  }

  const resolvedComponentId = input.componentId ?? actor.rootComponent?.uuid;
  if (!resolvedComponentId) {
    return {
      ok: false,
      action: input.action,
      mode,
      status: 'blocked',
      plannedChanges: [],
      affectedComponentIds: [],
      missingComponentIds: [],
      undoGroupId: null,
      undoUnavailableReason: 'Actor has no root component to target.',
      warnings: [`Actor ${actorId} has no root component; supply componentId explicitly.`],
    };
  }

  const component = getComponent(actorId, resolvedComponentId);
  if (!component) {
    return {
      ok: false,
      action: input.action,
      mode,
      status: 'blocked',
      plannedChanges: [],
      affectedComponentIds: [],
      missingComponentIds: [resolvedComponentId],
      undoGroupId: null,
      undoUnavailableReason: 'Target component was not found.',
      warnings: [`Component ${resolvedComponentId} was not found on actor ${actorId}.`],
    };
  }

  const resolvedInput: ComponentActionInput = {
    ...input,
    componentId: resolvedComponentId,
  };

  if (input.action === 'setEnabled') {
    if (typeof input.enabled !== 'boolean') {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        affectedComponentIds: [resolvedComponentId],
        missingComponentIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'setEnabled requires enabled.',
        warnings: ['setEnabled requires enabled.'],
      };
    }
    return executeComponentPropertyChange(resolvedInput, component, { enabled: input.enabled });
  }

  if (!input.properties || Object.keys(input.properties).length === 0) {
    return {
      ok: false,
      action: input.action,
      mode,
      status: 'blocked',
      plannedChanges: [],
      affectedComponentIds: [resolvedComponentId],
      missingComponentIds: [],
      undoGroupId: null,
      undoUnavailableReason: 'setProperties requires at least one property.',
      warnings: ['setProperties requires at least one property.'],
    };
  }
  return executeComponentPropertyChange(resolvedInput, component, input.properties);
}

async function executeComponentPropertyChange(
  input: ComponentActionInput,
  component: ENGINE.SceneComponent,
  properties: Record<string, unknown>,
): Promise<ComponentActionResult> {
  const dryRun = input.dryRun ?? false;
  const mode = dryRun ? 'dryRun' : 'apply';
  const actorId = input.actorId!;
  const componentId = input.componentId!;
  const previousValues = Object.fromEntries(
    Object.keys(properties).map(path => [path, getPathValue(component, path)]),
  );
  const plannedChanges = [{ kind: input.action, actorId, componentId, properties, previousValues }];

  if (dryRun) {
    return {
      ok: true,
      action: input.action,
      mode,
      status: 'planned',
      plannedChanges,
      affectedComponentIds: [componentId],
      missingComponentIds: [],
      undoGroupId: null,
      undoUnavailableReason: null,
      warnings: [],
    };
  }

  const undoRedo = editorStore.undoRedoManager;
  const beforeIndex = undoRedo.getCurrentAction();
  await undoRedo.recordAction(
    input.action === 'setEnabled' ? 'Set Component Enabled' : 'Set Component Properties',
    () => { setComponentProperties(component, properties); },
    () => { setComponentProperties(component, previousValues); },
  );
  const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Component property change did not register an undo entry.');
  const result: ComponentActionResult = {
    ok: true,
    action: input.action,
    mode,
    status: 'applied',
    plannedChanges,
    affectedComponentIds: [componentId],
    missingComponentIds: [],
    undoGroupId: undo.undoGroupId,
    undoUnavailableReason: undo.undoUnavailableReason,
    warnings: [],
  };
  return { ...result, warnings: await saveAfterComponentMutation(mode, result) };
}

async function saveAfterComponentMutation(
  mode: 'dryRun' | 'apply',
  result: ComponentActionResult,
): Promise<string[]> {
  if (mode !== 'apply' || !result.ok || result.status !== 'applied') {
    return result.warnings;
  }

  try {
    const saved = await editorSave(false, false);
    if (!saved) {
      return [...result.warnings, 'Component mutation applied but save did not complete.'];
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return [...result.warnings, `Component mutation applied but save failed: ${message}`];
  }
  return result.warnings;
}

async function executeEditorAction(input: EditorActionInput): Promise<EditorActionResult> {
  const dryRun = input.dryRun ?? false;
  const mode = dryRun ? 'dryRun' : 'apply';

  if (input.action === 'frameSelection') {
    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges: [{ kind: 'frameSelection' }],
        undoGroupId: null,
        undoUnavailableReason: 'Frame selection is not tracked in undo history.',
        warnings: [],
      };
    }
    focusSelectedObjects();
    return {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges: [{ kind: 'frameSelection' }],
      undoGroupId: null,
      undoUnavailableReason: 'Frame selection is not tracked in undo history.',
      warnings: [],
    };
  }

  if (input.action === 'enterPlayMode') {
    const isActive = editorStore.scene.isPlayMode;
    const isStarting = editorStore.scene.isPlayModeStarting;
    if (isActive || isStarting) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        undoGroupId: null,
        undoUnavailableReason: 'Play mode is not undoable.',
        warnings: [isStarting ? 'Play mode is already starting.' : 'Play mode is already active.'],
      };
    }
    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges: [{ kind: 'enterPlayMode' }],
        undoGroupId: null,
        undoUnavailableReason: 'Play mode is not undoable.',
        warnings: [],
      };
    }
    try {
      await setPlayModeActive(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        undoGroupId: null,
        undoUnavailableReason: 'Play mode is not undoable.',
        warnings: [`Failed to enter play mode: ${message}`],
      };
    }

    if (!editorStore.scene.isPlayMode) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        undoGroupId: null,
        undoUnavailableReason: 'Play mode is not undoable.',
        warnings: ['Play mode did not start. Check save/build diagnostics for details.'],
      };
    }

    return {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges: [{ kind: 'enterPlayMode' }],
      undoGroupId: null,
      undoUnavailableReason: 'Play mode is not undoable.',
      warnings: [],
    };
  }

  if (input.action === 'captureScreenshot') {
    const imageType = input.imageType?.trim() || 'image/jpeg';
    const imageQuality = typeof input.imageQuality === 'number' ? input.imageQuality : 0.9;
    const includeImage = input.includeImage ?? false;
    const scenePath = input.scenePath?.trim() || editorStore.project.currentScene;
    const filename = input.filename?.trim() || null;
    const width = typeof input.width === 'number' ? input.width : null;
    const height = typeof input.height === 'number' ? input.height : null;
    const updateThumbnails = input.updateThumbnails ?? false;
    const plannedChanges = [{
      kind: 'captureScreenshot',
      scenePath,
      imageType,
      imageQuality,
      includeImage,
      ...(filename != null ? { filename } : {}),
      ...(width != null ? { width } : {}),
      ...(height != null ? { height } : {}),
      updateThumbnails,
    }];

    if ((width == null) !== (height == null)) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        undoGroupId: null,
        undoUnavailableReason: 'Screenshot capture is not tracked in undo history.',
        warnings: ['width and height must both be provided together, or both omitted.'],
      };
    }

    if (editorStore.scene.isPrefabMode) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        undoGroupId: null,
        undoUnavailableReason: 'Screenshots are blocked while editing a prefab.',
        warnings: ['Exit prefab isolation before capturing a scene screenshot.'],
      };
    }

    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        undoGroupId: null,
        undoUnavailableReason: 'Screenshot capture is not tracked in undo history.',
        warnings: [],
        screenshot: {
          scenePath: scenePath ?? null,
          projectScreenshotKey: null,
          sceneScreenshotKey: null,
          projectScreenshotPath: null,
          sceneScreenshotPath: null,
          screenshotPath: null,
          requestedFilename: filename,
          captureWidth: width,
          captureHeight: height,
          imageType,
          imageByteLength: null,
          imageIncluded: includeImage,
          imageBase64: null,
        },
      };
    }

    let captureResult;
    try {
      captureResult = await editorStore.scene.captureScreenshotAsync(
        scenePath,
        imageType,
        imageQuality,
        includeImage,
        filename,
        width,
        height,
        updateThumbnails,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges,
        undoGroupId: null,
        undoUnavailableReason: 'Screenshot capture failed.',
        warnings: [`Failed to capture screenshot: ${message}`],
        screenshot: {
          scenePath: scenePath ?? null,
          projectScreenshotKey: null,
          sceneScreenshotKey: null,
          projectScreenshotPath: null,
          sceneScreenshotPath: null,
          screenshotPath: null,
          requestedFilename: filename,
          captureWidth: width,
          captureHeight: height,
          imageType,
          imageByteLength: null,
          imageIncluded: includeImage,
          imageBase64: null,
        },
      };
    }
    if (!captureResult.ok) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges,
        undoGroupId: null,
        undoUnavailableReason: 'Screenshot capture failed.',
        warnings: captureResult.warnings,
        screenshot: {
          scenePath: captureResult.scenePath,
          projectScreenshotKey: captureResult.projectScreenshotKey,
          sceneScreenshotKey: captureResult.sceneScreenshotKey,
          projectScreenshotPath: captureResult.projectScreenshotPath,
          sceneScreenshotPath: captureResult.sceneScreenshotPath,
          screenshotPath: captureResult.screenshotPath,
          requestedFilename: captureResult.requestedFilename,
          captureWidth: captureResult.captureWidth,
          captureHeight: captureResult.captureHeight,
          imageType,
          imageByteLength: captureResult.imageByteLength,
          imageIncluded: includeImage,
          imageBase64: captureResult.imageBase64,
        },
      };
    }

    return {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges,
      undoGroupId: null,
      undoUnavailableReason: 'Screenshot capture is not tracked in undo history.',
      warnings: captureResult.warnings,
      screenshot: {
        scenePath: captureResult.scenePath,
        projectScreenshotKey: captureResult.projectScreenshotKey,
        sceneScreenshotKey: captureResult.sceneScreenshotKey,
        projectScreenshotPath: captureResult.projectScreenshotPath,
        sceneScreenshotPath: captureResult.sceneScreenshotPath,
        screenshotPath: captureResult.screenshotPath,
        requestedFilename: captureResult.requestedFilename,
        captureWidth: captureResult.captureWidth,
        captureHeight: captureResult.captureHeight,
        imageType,
        imageByteLength: captureResult.imageByteLength,
        imageIncluded: includeImage,
        imageBase64: captureResult.imageBase64,
      },
    };
  }

  if (input.action === 'exitPlayMode') {
    const isActive = editorStore.scene.isPlayMode;
    const isStarting = editorStore.scene.isPlayModeStarting;
    if (!isActive && !isStarting) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        undoGroupId: null,
        undoUnavailableReason: 'Play mode is not undoable.',
        warnings: ['Play mode is not active.'],
      };
    }
    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges: [{ kind: 'exitPlayMode' }],
        undoGroupId: null,
        undoUnavailableReason: 'Play mode is not undoable.',
        warnings: [],
      };
    }
    try {
      await setPlayModeActive(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        undoGroupId: null,
        undoUnavailableReason: 'Play mode is not undoable.',
        warnings: [`Failed to exit play mode: ${message}`],
      };
    }
    return {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges: [{ kind: 'exitPlayMode' }],
      undoGroupId: null,
      undoUnavailableReason: 'Play mode is not undoable.',
      warnings: [],
    };
  }

  return {
    ok: false,
    action: input.action,
    mode,
    status: 'blocked',
    plannedChanges: [],
    undoGroupId: null,
    undoUnavailableReason: 'Unknown editor action.',
    warnings: [`Unknown editor action: ${input.action}`],
  };
}

function resolvePrefabPathInput(prefabPath: string | undefined): string | null {
  const normalised = normalizePrefabAssetPath(prefabPath ?? '');
  if (!normalised) {
    return null;
  }
  return normalised.endsWith(ENGINE.PREFAB_EXTENSION) ? normalised : `${normalised}${ENGINE.PREFAB_EXTENSION}`;
}

async function executePrefabAction(input: PrefabActionInput): Promise<PrefabActionResult> {
  const dryRun = input.dryRun ?? false;
  const mode = dryRun ? 'dryRun' : 'apply';
  const world = editorStore.scene.world;

  if (!world) {
    return {
      ok: false,
      action: input.action,
      mode,
      status: 'blocked',
      plannedChanges: [],
      prefabPath: null,
      affectedActorIds: [],
      missingActorIds: [],
      undoGroupId: null,
      undoUnavailableReason: 'Editor world is not loaded.',
      warnings: ['Editor world is not loaded.'],
    };
  }

  if (editorStore.scene.isPlayMode) {
    return {
      ok: false,
      action: input.action,
      mode,
      status: 'blocked',
      plannedChanges: [],
      prefabPath: null,
      affectedActorIds: [],
      missingActorIds: [],
      undoGroupId: null,
      undoUnavailableReason: 'Prefab actions are blocked while play mode is active.',
      warnings: ['Stop play mode before mutating prefabs.'],
    };
  }

  if (input.action === 'createFromActor') {
    const actorId = input.actorId?.trim();
    if (!actorId) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        prefabPath: null,
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'createFromActor requires actorId.',
        warnings: ['createFromActor requires actorId.'],
      };
    }

    const actor = world.getActorByUuid(actorId);
    const outlinerNode = editorStore.scene.outliner.getNodeById(actorId);
    if (!actor || !outlinerNode) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        prefabPath: null,
        affectedActorIds: [],
        missingActorIds: [actorId],
        undoGroupId: null,
        undoUnavailableReason: 'Target actor was not found.',
        warnings: [`Actor ${actorId} was not found.`],
      };
    }

    const existingPrefabName = actor.getPrefabName();
    if (existingPrefabName && existingPrefabName.trim() !== '') {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        prefabPath: existingPrefabName,
        affectedActorIds: [actorId],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'Actor is already a prefab instance.',
        warnings: [`Actor ${actorId} is already linked to prefab ${existingPrefabName}.`],
      };
    }

    const targetDirectory = resolvePrefabCreateDirectory(
      input.parentPath,
      assetsStore.currentPrefabDirectory,
    );
    const preferredName = input.preferredName?.trim() || actor.name || 'Prefab';
    const plannedChanges = [{
      kind: 'createFromActor',
      actorId,
      parentPath: targetDirectory,
      preferredName,
    }];

    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        prefabPath: null,
        affectedActorIds: [actorId],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: null,
        warnings: [],
      };
    }

    await createPrefabFromObject(outlinerNode, targetDirectory, preferredName);
    const prefabPath = actor.getPrefabName() || null;

    const result: PrefabActionResult = {
      ok: !!prefabPath,
      action: input.action,
      mode,
      status: prefabPath ? 'applied' : 'blocked',
      plannedChanges: prefabPath ? [{ kind: 'createFromActor', actorId, prefabPath }] : plannedChanges,
      prefabPath,
      affectedActorIds: [actorId],
      missingActorIds: [],
      undoGroupId: null,
      undoUnavailableReason: 'Creating a prefab asset is not currently tracked in the editor undo stack.',
      warnings: prefabPath ? [] : ['Prefab asset path was not set on the actor after creation.'],
    };

    return result;
  }

  if (input.action === 'instantiate') {
    const prefabPath = resolvePrefabPathInput(input.prefabPath);
    if (!prefabPath) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        prefabPath: null,
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'instantiate requires prefabPath.',
        warnings: ['instantiate requires prefabPath.'],
      };
    }

    const transform = {
      position: input.position,
      rotation: input.rotation,
      scale: input.scale,
    };
    const plannedChanges = [{ kind: 'instantiate', prefabPath, transform }];

    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        prefabPath,
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: null,
        warnings: [],
      };
    }

    const loaded = await ensurePrefabAsset(prefabPath);
    if (!loaded) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges,
        prefabPath,
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'Prefab asset could not be loaded.',
        warnings: [`Failed to load prefab asset: ${prefabPath}`],
      };
    }

    const position = input.position
      ? new THREE.Vector3(input.position[0], input.position[1], input.position[2])
      : undefined;
    const rotation = input.rotation
      ? new THREE.Euler(input.rotation[0], input.rotation[1], input.rotation[2])
      : undefined;
    const scale = input.scale
      ? new THREE.Vector3(input.scale[0], input.scale[1], input.scale[2])
      : undefined;

    const outlinerPlacement = captureDefaultOutlinerPlacementIntent();
    const undoRedo = editorStore.undoRedoManager;
    const beforeIndex = undoRedo.getCurrentAction();
    let placedActors: ENGINE.Actor[] = [];

    await undoRedo.recordAction(
      'Instantiate Prefab',
      async () => {
        const actors = await ENGINE.WorldCommands.placePrefabs({
          world,
          prefabs: [{ path: prefabPath, transform: { position, rotation, scale } }],
        });
        placedActors = actors.filter((a): a is ENGINE.Actor => a != null);

        editorStore.scene.updateOutliner();
        const root = editorStore.scene.outliner.rootNode ?? undefined;
        nameHelpers.assignUniqueActorDisplayNames(placedActors, root, { getAssetPathHint: () => prefabPath });
        for (const placed of placedActors) {
          editorStore.scene.outliner.getNodeById(placed.uuid)?.setName(placed.name);
        }
        applyDefaultOutlinerPlacementIntent(placedActors.map(a => a.uuid), outlinerPlacement);
        if (placedActors[0]) {
          editorStore.scene.selectObject(placedActors[0]);
        }
      },
      () => {
        editorStore.scene.removeObjects(...placedActors);
      },
    );

    if (placedActors.length === 0) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges,
        prefabPath,
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'Prefab instantiation failed.',
        warnings: [`No actors were created from prefab ${prefabPath}.`],
      };
    }

    const placedUuids = placedActors.map(a => a.uuid);

    const undo = getUndoGroupResult(undoRedo, beforeIndex, 'Prefab instantiation did not register an undo entry.');
    const result: PrefabActionResult = {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges: [{ kind: 'instantiate', prefabPath, actorIds: placedUuids }],
      prefabPath,
      affectedActorIds: placedUuids,
      missingActorIds: [],
      undoGroupId: undo.undoGroupId,
      undoUnavailableReason: undo.undoUnavailableReason,
      warnings: [],
    };

    try {
      const saved = await editorSave(false, false);
      if (!saved) {
        result.warnings = [...result.warnings, 'Prefab instantiated but scene save did not complete.'];
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      result.warnings = [...result.warnings, `Prefab instantiated but scene save failed: ${message}`];
    }

    return result;
  }

  if (input.action === 'apply' || input.action === 'unpack') {
    const actorId = input.actorId?.trim();
    if (!actorId) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        prefabPath: null,
        affectedActorIds: [],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: `${input.action} requires actorId.`,
        warnings: [`${input.action} requires actorId.`],
      };
    }

    const actor = world.getActorByUuid(actorId);
    const outlinerNode = editorStore.scene.outliner.getNodeById(actorId);
    if (!actor || !outlinerNode) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        prefabPath: null,
        affectedActorIds: [],
        missingActorIds: [actorId],
        undoGroupId: null,
        undoUnavailableReason: 'Target actor was not found.',
        warnings: [`Actor ${actorId} was not found.`],
      };
    }

    const prefabPath = actor.getPrefabName()?.trim() || null;
    if (!prefabPath) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        prefabPath: null,
        affectedActorIds: [actorId],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: 'Actor is not a prefab instance.',
        warnings: [`Actor ${actorId} is not linked to a prefab asset.`],
      };
    }

    const plannedChanges = [{ kind: input.action, actorId, prefabPath }];
    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        prefabPath,
        affectedActorIds: [actorId],
        missingActorIds: [],
        undoGroupId: null,
        undoUnavailableReason: null,
        warnings: [],
      };
    }

    const actionCompleted = input.action === 'apply'
      ? await applyToPrefabAsset(outlinerNode)
      : await unlinkPrefabFromActor(outlinerNode);

    const updatedPrefabPath = actor.getPrefabName()?.trim() || null;
    const applySucceeded = actionCompleted && (input.action === 'apply' ? updatedPrefabPath === prefabPath : updatedPrefabPath === null);

    return {
      ok: applySucceeded,
      action: input.action,
      mode,
      status: applySucceeded ? 'applied' : 'blocked',
      plannedChanges: [{ kind: input.action, actorId, prefabPath: updatedPrefabPath ?? prefabPath }],
      prefabPath: updatedPrefabPath ?? prefabPath,
      affectedActorIds: [actorId],
      missingActorIds: [],
      undoGroupId: null,
      undoUnavailableReason: input.action === 'apply'
        ? 'Applying instance changes to a prefab asset is not tracked in the editor undo stack.'
        : 'Unpacking a prefab instance is not tracked in the editor undo stack.',
      warnings: applySucceeded
        ? []
        : [`Prefab ${input.action} may have failed; verify the instance in the editor.`],
    };
  }

  return {
    ok: false,
    action: input.action,
    mode,
    status: 'blocked',
    plannedChanges: [],
    prefabPath: null,
    affectedActorIds: [],
    missingActorIds: [],
    undoGroupId: null,
    undoUnavailableReason: 'Unknown prefab action.',
    warnings: [`Unknown prefab action: ${input.action}`],
  };
}

async function executeSceneAction(input: SceneActionInput): Promise<SceneActionResult> {
  const dryRun = input.dryRun ?? false;
  const mode = dryRun ? 'dryRun' : 'apply';

  if (input.action === 'open' || input.action === 'setActive') {
    const scenePath = input.scenePath?.trim();
    if (!scenePath) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        undoGroupId: null,
        undoUnavailableReason: 'Scene switching is not undoable.',
        warnings: [`${input.action} requires scenePath.`],
      };
    }

    const plannedChanges = [{ kind: input.action, scenePath, fromScenePath: editorStore.project.currentScene }];
    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges,
        undoGroupId: null,
        undoUnavailableReason: 'Scene switching is not undoable.',
        warnings: [],
      };
    }

    try {
      await sceneHandler.loadScene(ENGINE.AssetPath.fromString(scenePath), false, { recreateCanvas: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges,
        undoGroupId: null,
        undoUnavailableReason: 'Scene switching is not undoable.',
        warnings: [`Failed to open scene: ${message}`],
      };
    }

    return {
      ok: true,
      action: input.action,
      mode,
      status: 'applied',
      plannedChanges,
      undoGroupId: null,
      undoUnavailableReason: 'Scene switching is not undoable.',
      warnings: [],
    };
  }

  if (input.action === 'save') {
    const hasProject = editorStore.project.isProjectOpened;
    const hasCurrentScene = !!editorStore.project.currentScene;

    if (!hasProject || !hasCurrentScene) {
      return {
        ok: false,
        action: input.action,
        mode,
        status: 'blocked',
        plannedChanges: [],
        undoGroupId: null,
        undoUnavailableReason: 'Save is not undoable.',
        warnings: [!hasProject ? 'No project is open.' : 'No active scene to save.'],
      };
    }

    if (dryRun) {
      return {
        ok: true,
        action: input.action,
        mode,
        status: 'planned',
        plannedChanges: [{ kind: 'save', scenePath: editorStore.project.currentScene }],
        undoGroupId: null,
        undoUnavailableReason: 'Save is not undoable.',
        warnings: [],
      };
    }

    const saved = await editorSave(false, false);
    return {
      ok: saved,
      action: input.action,
      mode,
      status: saved ? 'applied' : 'blocked',
      plannedChanges: [{ kind: 'save', scenePath: editorStore.project.currentScene }],
      undoGroupId: null,
      undoUnavailableReason: 'Save is not undoable.',
      warnings: saved ? [] : ['Save did not complete. The editor may be busy or have a render error.'],
    };
  }

  return {
    ok: false,
    action: input.action,
    mode,
    status: 'blocked',
    plannedChanges: [],
    undoGroupId: null,
    undoUnavailableReason: 'Unknown scene action.',
    warnings: [`Unknown scene action: ${input.action}`],
  };
}

export const editorApi = {
  getSceneGraph(_sceneName?: string): SceneGraphNode[] {
    return getOutlinerState();
  },

  findActors(sceneName?: string, query?: string, limit?: number): SceneGraphNode[] {
    const nodes = editorApi.getSceneGraph(sceneName);
    const normalisedQuery = (query ?? '').trim().toLowerCase();
    const clampedLimit = Math.max(1, Math.min(200, limit ?? 50));
    const filtered = normalisedQuery
      ? nodes.filter(node => node.name.toLowerCase().includes(normalisedQuery) || node.id.toLowerCase().includes(normalisedQuery))
      : nodes;
    return filtered.slice(0, clampedLimit);
  },

  getSelectedActorIds(): string[] {
    return editorStore.scene.selectedActors.map(actor => actor.uuid);
  },

  getActorDetails(actorIds: string[], shape?: ActorQueryShapeOptions): unknown[] {
    const world = editorStore.scene.world;
    if (!world) return [];

    const resolvedShape = shape ?? createDefaultActorQueryShape();
    const details: unknown[] = [];
    for (const actorId of actorIds) {
      const actor = world.getActorByUuid(actorId);
      if (!actor) {
        details.push({ id: actorId, error: 'Actor not found' });
        continue;
      }
      try {
        const serialised = serializeActorForQuery(
          actor,
          actorId,
          resolvedShape,
          value => toPipeSafeValue(value),
          (value, options) => serializeDescribe(value, options),
        );
        details.push(toPipeSafeValue(serialised));
      } catch {
        details.push({ id: actorId, error: 'Failed to serialise actor details' });
      }
    }
    return details;
  },

  getActiveScenePath(): string | null {
    return editorStore.project.currentScene ?? null;
  },

  getEditorState() {
    return getEditorStateSnapshot();
  },

  getViewport() {
    return getViewportSnapshot();
  },

  getSceneSummary() {
    return getSceneSummarySnapshot();
  },

  getBuildErrors() {
    return getBuildErrorsSnapshot();
  },

  getConsoleErrors() {
    return getConsoleErrorsSnapshot();
  },

  getGeometryCatalog() {
    return getGeometryCatalogSnapshot();
  },
};

const ALL_ACTOR_QUERY_SHAPE_FIELDS: ActorQueryShapeField[] = [
  'describe',
  'transform',
  'boundingBox',
  'components',
  'childIds',
  'references',
  'editableProperties',
];

const GET_DETAILS_QUERY_FIELDS: ActorQueryShapeField[] = ALL_ACTOR_QUERY_SHAPE_FIELDS.filter(
  field => field !== 'editableProperties',
);

const ACTOR_QUERY_OPERATION_FIELDS: Record<string, ActorQueryShapeField[]> = {
  getDetails: GET_DETAILS_QUERY_FIELDS,
  getTransform: ['transform'],
  getBounds: ['transform', 'boundingBox'],
  getChildren: ['childIds'],
  getComponents: ['components'],
  getReferences: ['references'],
  getEditableProperties: ['editableProperties'],
};

function createDefaultActorQueryShape(): ActorQueryShapeOptions {
  return {
    fields: new Set(ALL_ACTOR_QUERY_SHAPE_FIELDS),
    includeComponentsDetails: true,
  };
}

function resolveActorQueryShapeFromParams(params: Record<string, unknown>): ActorQueryShapeOptions {
  const operation = typeof params['operation'] === 'string' ? params['operation'] : 'getDetails';
  const baseFields = ACTOR_QUERY_OPERATION_FIELDS[operation] ?? ALL_ACTOR_QUERY_SHAPE_FIELDS;
  const fields = new Set(baseFields);

  const include = Array.isArray(params['include'])
    ? params['include'].filter((value): value is string => typeof value === 'string')
    : [];
  for (const token of include) {
    if ((ALL_ACTOR_QUERY_SHAPE_FIELDS as string[]).includes(token)) {
      fields.add(token as ActorQueryShapeField);
    }
  }

  const exclude = Array.isArray(params['exclude'])
    ? params['exclude'].filter((value): value is string => typeof value === 'string')
    : [];
  for (const token of exclude) {
    fields.delete(token as ActorQueryShapeField);
  }

  return {
    fields,
    includeComponentsDetails: operation === 'getDetails' && fields.has('describe'),
  };
}

function toNumberTriple(raw: unknown): [number, number, number] | undefined {
  if (!Array.isArray(raw) || raw.length < 3) {
    return undefined;
  }
  const [a, b, c] = raw;
  if (
    typeof a !== 'number' || !Number.isFinite(a)
    || typeof b !== 'number' || !Number.isFinite(b)
    || typeof c !== 'number' || !Number.isFinite(c)
  ) {
    return undefined;
  }
  return [a, b, c];
}

export async function handleEditorApiQuery(method: string, params: Record<string, unknown>): Promise<unknown> {
  const result = await (async () => {
    switch (method) {
      case 'editor-api/getSceneGraph': {
        const sceneName = typeof params['sceneName'] === 'string' ? params['sceneName'] : undefined;
        return editorApi.getSceneGraph(sceneName);
      }
      case 'editor-api/findActors': {
        const sceneName = typeof params['sceneName'] === 'string' ? params['sceneName'] : undefined;
        const query = typeof params['query'] === 'string' ? params['query'] : undefined;
        const limit = typeof params['limit'] === 'number' && Number.isFinite(params['limit']) ? params['limit'] : undefined;
        return editorApi.findActors(sceneName, query, limit);
      }
      case 'editor-api/getSelectedActors': {
        return { actorIds: editorApi.getSelectedActorIds() };
      }
      case 'editor-api/getActorDetails': {
        const rawIds = params['actorIds'];
        if (!Array.isArray(rawIds)) {
          throw new Error('Missing required param: actorIds');
        }
        const actorIds = rawIds.filter((id): id is string => typeof id === 'string');
        if (actorIds.length === 0) {
          throw new Error('actorIds must contain at least one string id');
        }
        const shape = resolveActorQueryShapeFromParams(params);
        return { actors: editorApi.getActorDetails(actorIds, shape) };
      }
      case 'editor-api/getEditorState': {
        return editorApi.getEditorState();
      }
      case 'editor-api/getViewport': {
        return editorApi.getViewport();
      }
      case 'editor-api/getActiveScenePath': {
        return { activeScenePath: editorApi.getActiveScenePath() };
      }
      case 'editor-api/getSceneSummary': {
        return editorApi.getSceneSummary();
      }
      case 'editor-api/getBuildErrors': {
        return editorApi.getBuildErrors();
      }
      case 'editor-api/getConsoleErrors': {
        return editorApi.getConsoleErrors();
      }
      case 'editor-api/getGeometryCatalog': {
        return editorApi.getGeometryCatalog();
      }
      case 'editor-api/undoGroup': {
        const action = params['action'];
        if (action !== 'begin' && action !== 'end') {
          throw new Error('Invalid action for editor-api/undoGroup');
        }
        const name = typeof params['name'] === 'string' ? params['name'] : undefined;
        return executeUndoGroupAction({ action, name });
      }
      case 'editor-api/actionActor': {
        const action = params['action'];
        if (
          action !== 'select' && action !== 'focus' && action !== 'rename'
          && action !== 'create' && action !== 'createMany' && action !== 'duplicate' && action !== 'delete'
          && action !== 'setTransform' && action !== 'reparent'
        ) {
          throw new Error('Invalid action for editor-api/actionActor');
        }
        const actorIds = Array.isArray(params['actorIds'])
          ? params['actorIds'].filter((id): id is string => typeof id === 'string')
          : undefined;
        const actorId = typeof params['actorId'] === 'string' ? params['actorId'] : undefined;
        const name = typeof params['name'] === 'string' ? params['name'] : undefined;
        const className = typeof params['className'] === 'string' ? params['className'] : undefined;
        const primitiveType = typeof params['primitiveType'] === 'string' ? params['primitiveType'] : undefined;
        const assetPath = typeof params['assetPath'] === 'string' ? params['assetPath'] : undefined;
        const targetParentId = typeof params['targetParentId'] === 'string' ? params['targetParentId'] : null;
        const dryRun = typeof params['dryRun'] === 'boolean' ? params['dryRun'] : undefined;
        const payload = params['payload'];
        const position = toNumberTriple(params['position']);
        const rotation = toNumberTriple(params['rotation']);
        const scale = toNumberTriple(params['scale']);
        return await executeActorAction({
          action,
          actorIds,
          actorId,
          name,
          className,
          primitiveType,
          assetPath,
          targetParentId,
          position,
          rotation,
          scale,
          payload,
          dryRun,
        });
      }
      case 'editor-api/actionComponent': {
        const action = params['action'];
        if (action !== 'add' && action !== 'setProperties' && action !== 'setEnabled') {
          throw new Error('Invalid action for editor-api/actionComponent');
        }
        const actorId = typeof params['actorId'] === 'string' ? params['actorId'] : undefined;
        const componentId = typeof params['componentId'] === 'string' ? params['componentId'] : undefined;
        const parentComponentId = typeof params['parentComponentId'] === 'string' ? params['parentComponentId'] : undefined;
        const className = typeof params['className'] === 'string' ? params['className'] : undefined;
        const properties = params['properties'] && typeof params['properties'] === 'object' && !Array.isArray(params['properties'])
          ? params['properties'] as Record<string, unknown>
          : undefined;
        const enabled = typeof params['enabled'] === 'boolean' ? params['enabled'] : undefined;
        const dryRun = typeof params['dryRun'] === 'boolean' ? params['dryRun'] : undefined;
        return await executeComponentAction({ action, actorId, componentId, parentComponentId, className, properties, enabled, dryRun });
      }
      case 'editor-api/actionEditor': {
        const action = params['action'];
        if (action !== 'frameSelection' && action !== 'enterPlayMode' && action !== 'exitPlayMode' && action !== 'captureScreenshot') {
          throw new Error('Invalid action for editor-api/actionEditor');
        }
        const dryRun = typeof params['dryRun'] === 'boolean' ? params['dryRun'] : undefined;
        const scenePath = typeof params['scenePath'] === 'string' ? params['scenePath'] : undefined;
        const imageType = typeof params['imageType'] === 'string' ? params['imageType'] : undefined;
        const imageQuality = typeof params['imageQuality'] === 'number' ? params['imageQuality'] : undefined;
        const includeImage = typeof params['includeImage'] === 'boolean' ? params['includeImage'] : undefined;
        const filename = typeof params['filename'] === 'string' ? params['filename'] : undefined;
        const width = typeof params['width'] === 'number' ? params['width'] : undefined;
        const height = typeof params['height'] === 'number' ? params['height'] : undefined;
        const updateThumbnails = typeof params['updateThumbnails'] === 'boolean' ? params['updateThumbnails'] : undefined;
        return await executeEditorAction({ action, dryRun, scenePath, imageType, imageQuality, includeImage, filename, width, height, updateThumbnails });
      }
      case 'editor-api/actionScene': {
        const action = params['action'];
        if (action !== 'open' && action !== 'save' && action !== 'setActive') {
          throw new Error('Invalid action for editor-api/actionScene');
        }
        const scenePath = typeof params['scenePath'] === 'string' ? params['scenePath'] : undefined;
        const dryRun = typeof params['dryRun'] === 'boolean' ? params['dryRun'] : undefined;
        return await executeSceneAction({ action, scenePath, dryRun });
      }
      case 'editor-api/actionPrefab': {
        const action = params['action'];
        if (action !== 'createFromActor' && action !== 'instantiate' && action !== 'apply' && action !== 'unpack') {
          throw new Error('Invalid action for editor-api/actionPrefab');
        }
        const actorId = typeof params['actorId'] === 'string' ? params['actorId'] : undefined;
        const prefabPath = typeof params['prefabPath'] === 'string' ? params['prefabPath'] : undefined;
        const parentPath = typeof params['parentPath'] === 'string' ? params['parentPath'] : undefined;
        const preferredName = typeof params['preferredName'] === 'string' ? params['preferredName'] : undefined;
        const dryRun = typeof params['dryRun'] === 'boolean' ? params['dryRun'] : undefined;
        const position = toNumberTriple(params['position']);
        const rotation = toNumberTriple(params['rotation']);
        const scale = toNumberTriple(params['scale']);
        return await executePrefabAction({
          action,
          actorId,
          prefabPath,
          parentPath,
          preferredName,
          position,
          rotation,
          scale,
          dryRun,
        });
      }
      default:
        throw new Error(`Unknown editor-api method: ${method}`);
    }
  })();

  return toPipeSafeValue(result);
}
